import { Menu } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { useTheme } from '../../context/ThemeContext';
import { SidebarChatMenu } from '../chat/components/SidebarChatMenu';
import { isAdminSession } from '../admin/utils/adminPreview';
import NotificationDock from '../notifications/NotificationDock';
import { useChatSessions, ChatSession } from '../../context/ChatSessionsContext';
import { useBilling } from '../../context/BillingContext';
import AppSidebar from './AppSidebar';
import styles from './AppLayout.module.css';


export const AppLayout: React.FC = () => {
  const { user, token, logout } = useAuth();
  const { brand, logo: brandLogo } = useBranding();   // SRS #38 white-labelling
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { sessions, pinnedSessions, unpinnedSessions, refreshSessions, updateTitle, deleteSession, togglePin } = useChatSessions();
  const billing = useBilling();
  const { isTrialing, trialDaysRemaining, subscription } = billing;
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > 960);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [sessionSummaries, setSessionSummaries] = useState<Record<number, string>>({});
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [showViewToggle, setShowViewToggle] = useState(false);

  const api_url = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
  // The dashboard renders the shared sidebar itself, so this one stands down
  // there to avoid two of them. Nothing else behind sign-in goes without:
  // Billing, Search, the search results and the search history each used to
  // suppress it, and a customer who opened one lost the menu entirely.
  const isDashboardView = location.pathname.startsWith('/dashboard');

  // Fetch a short summary for a session by loading its messages and picking a concise text
  const fetchSessionSummary = async (sessionId: number) => {
    if (!token) return;

    try {
      // Ask the backend to generate and persist a short title using the server-side OpenAI key
      const resp = await fetch(
        `${api_url}/api/v1/chat/sessions/${sessionId}/generate_title`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!resp.ok) return;
      const data = await resp.json().catch(() => ({}));
      const title = data?.title;
      if (title) {
        setSessionSummaries(prev => ({ ...prev, [sessionId]: title }));
      }
    } catch (err) {
      // ignore per-session failures
    }
  };

  // When sessions change, fetch lightweight summaries for them (non-blocking).
  // Always attempt to generate a nicer summary (preferring bot response),
  // but avoid duplicate fetches via a ref-set.
  const fetchingSummariesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (sessions.length === 0) return;
    // Only auto-generate titles for the 3 most recent untitled sessions.
    // Older sessions are titled lazily when the user interacts with them.
    const untitled = sessions
      .filter(s => !s.title && !sessionSummaries[s.id] && !fetchingSummariesRef.current.has(s.id))
      .slice(0, 3);

    untitled.forEach((s, idx) => {
      fetchingSummariesRef.current.add(s.id);
      setTimeout(async () => {
        try {
          await fetchSessionSummary(s.id);
        } finally {
          fetchingSummariesRef.current.delete(s.id);
        }
      }, idx * 400); // space them out more to avoid hitting rate limits
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  // Refresh sessions only on login (user email becoming available), not on every navigation
  useEffect(() => {
    if (user?.email) refreshSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  useEffect(() => {
    setShowViewToggle(isAdminSession());
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const mqMedium = window.matchMedia('(max-width: 960px)');
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      if (e.matches) setIsSidebarOpen(false);
    };
    const handlerMedium = (e: MediaQueryListEvent) => {
      if (e.matches && !window.matchMedia('(max-width: 768px)').matches) {
        setIsSidebarOpen(false);
      } else if (!e.matches) {
        setIsSidebarOpen(true);
      }
    };
    mq.addEventListener('change', handler);
    mqMedium.addEventListener('change', handlerMedium);
    return () => {
      mq.removeEventListener('change', handler);
      mqMedium.removeEventListener('change', handlerMedium);
    };
  }, []);

  const handleTitleEdit = (sessionId: number, currentTitle: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  const handleTitleSave = async (sessionId: number) => {
    if (!editingTitle.trim()) {
      setEditingSessionId(null);
      return;
    }

    try {
      await updateTitle(sessionId, editingTitle);
    } catch (error) {
      console.error('Error updating session title:', error);
    }
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const handleTogglePin = async (sessionId: number) => {
    try {
      await togglePin(sessionId);
    } catch (error) {
      console.error('Error toggling pin status:', error);
    }
  };

  const handleTitleCancel = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const handleDeleteSession = async (sessionId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();

    const confirmed = window.confirm('Are you sure you want to delete this chat? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteSession(sessionId);
      if (selectedSessionId === sessionId) {
        setSelectedSessionId(null);
        navigate('/app');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Error deleting session. Please try again.');
    }
  };




  const initials = (user?.full_name || user?.email || '?')[0]?.toUpperCase() ?? '?';
  const displayName = user?.full_name || user?.email || 'Policyora user';


  const renderChatItem = (session: ChatSession) => {
    const isActive = selectedSessionId === session.id;
    return (
      <li
        key={session.id}
        className={
          styles.chatListItem +
          (isActive ? ' ' + styles.chatListItemActive : '') +
          (session.is_pinned ? ' ' + styles.pinnedChatItem : '')
        }
        tabIndex={0}
        aria-current={isActive ? 'true' : undefined}
        title={session.title}
        onClick={() => {
          if (editingSessionId !== session.id) {
            setSelectedSessionId(session.id);
            navigate('/app');
          }
        }}
      >
        {editingSessionId === session.id ? (
          <div className={styles.editTitleContainer} onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              className={styles.editTitleInput}
              value={editingTitle}
              onChange={(e) => setEditingTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTitleSave(session.id);
                } else if (e.key === 'Escape') {
                  handleTitleCancel();
                }
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            
          </div>
        ) : (
          <>
            <div className={styles.chatTitleWrapper}>
              {session.is_pinned && (
                <span className={styles.pinIcon} title="Pinned">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <line x1="12" y1="17" x2="12" y2="22"/>
                    <path d="M5 17h14v-2a7 7 0 0 0-7-7 7 7 0 0 0-7 7v2z"/>
                    <path d="M12 10V3"/>
                  </svg>
                </span>
              )}
              <span className={styles.chatTitle}>
                {sessionSummaries[session.id] ?? session.title}
              </span>
            </div>
            <div className={styles.chatActions}>
              <SidebarChatMenu
                onRename={() => handleTitleEdit(session.id, session.title)}
                onDelete={() => handleDeleteSession(session.id)}
                onPinChat={() => handleTogglePin(session.id)}
                isPinned={session.is_pinned}
              />
            </div>
            
          </>
        )}
      </li>
    );
  };

  const renderSidebar = () => {
    if (isDashboardView) return null;

    return (
      <>
        {isMobile && (
          <>
            {!isSidebarOpen && (
              <button
                type="button"
                className={styles.mobileHamburger}
                aria-label="Open sidebar"
                onClick={() => setIsSidebarOpen(true)}
              >
                <Menu size={20} aria-hidden />
              </button>
            )}
            {isSidebarOpen && (
              <div className={styles.mobileOverlay} onClick={() => setIsSidebarOpen(false)} />
            )}
          </>
        )}
        <aside className={`${styles.sidebar} ${!isSidebarOpen ? styles.sidebarCollapsed : ''} ${isMobile ? (isSidebarOpen ? styles.mobileDrawerOpen : styles.mobileDrawer) : ''}`}>
            {/* White brand header at top — matches user dashboard sidebar */}
            <div className={styles.sidebarBrandHeader}>
              <button type='button' className={`${styles.brandButton} ${styles.brandButtonExpanded}`} onClick={() => navigate('/app')}>
                {brandLogo ? (
                  <span className={styles.brandCustom}>
                    <img src={brandLogo} alt={brand?.brand_name || 'Workspace logo'} className={styles.brandLogo} data-testid="brand-logo" />
                  </span>
                ) : (
                  <img
                    src='/Policy Ora Final Logo.png'
                    alt="Policyora"
                    className={styles.brandLogo}
                  />
                )}
              </button>
              <div className={styles.sidebarTopActions}>
                <button
                  type='button'
                  className={styles.sidebarIconButton}
                  aria-label='Hide sidebar'
                  onClick={() => setIsSidebarOpen(false)}
                >
                  ≡
                </button>
              </div>
              {/* Collapsed-only toggle */}
              <button
                type='button'
                className={styles.collapsedToggle}
                aria-label='Show sidebar'
                onClick={() => setIsSidebarOpen(true)}
                title='Expand sidebar'
              >
                ≡
              </button>
            </div>
            <div className={styles.sidebarContent}>
              <button type='button' className={styles.newChatButton} onClick={() => {
                setSelectedSessionId(null);
                navigate('/app');
              }}>
                + New chat
              </button>
              {/* The one menu (./navigation), rendered here inside this
                  sidebar's own chrome because it also carries the chat list.
                  It used to define its own fifteen entries, which shared only
                  three with the dashboard's. */}
              <div className={styles.navSection}>
                <AppSidebar navOnly />
              </div>
              {/* Pinned Chats Section */}
              {pinnedSessions.length > 0 ? (
                <>
                  <div className={styles.sectionLabel}>
                    Pinned chats
                  </div>
                  <div className={`${styles.chatListWrapper} ${styles.pinnedChats}`}>
                    <ul className={styles.chatList}>
                      {pinnedSessions.map((session) => renderChatItem(session))}
                    </ul>
                  </div>
                </>
              ) : (
                <div className={styles.sectionLabel}>No pinned chats</div>
              )}

              {/* All Chats Section */}
              <div className={styles.sectionLabel}>
                {pinnedSessions.length > 0 ? 'All chats' : 'Your chats'}
              </div>
              {unpinnedSessions.length > 0 ? (
                <div className={styles.chatListWrapper}>
                  <ul className={styles.chatList}>
                    {unpinnedSessions.map((session) => renderChatItem(session))}
                  </ul>
                </div>
              ) : (
                <div className={styles.noChatsMessage}>
                  No chats available. Create a new chat to get started.
                </div>
              )}
            </div>
            {brand && brand.source === 'workspace' && brand.platform_credit && (
              <div className={styles.poweredBy} data-testid="powered-by">Powered by Policyora</div>
            )}
            <div className={styles.sidebarFooter}>
              {/* Free Trial card — only for non-admin users currently on a trial */}
              {!isAdminSession() && isTrialing && (
                <button
                  type="button"
                  className={styles.trialCard}
                  onClick={() => navigate('/billing')}
                  title="View billing details"
                >
                  <span className={styles.trialDot} aria-hidden="true" />
                  <div className={styles.trialText}>
                    <div className={styles.trialTitle}>Free Trial Active</div>
                    <div className={styles.trialMeta}>
                      {subscription?.plan_tier
                        ? `${subscription.plan_tier.replace(/_/g, ' ')} plan`
                        : 'Trial plan'}
                      {typeof trialDaysRemaining === 'number' && trialDaysRemaining >= 0
                        ? ` · ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left`
                        : ''}
                    </div>
                  </div>
                  {typeof trialDaysRemaining === 'number' && trialDaysRemaining >= 0 && (
                    <span className={styles.trialBadge}>{trialDaysRemaining}d</span>
                  )}
                </button>
              )}

              {!isAdminSession() && (
                <div className={styles.userInfo}>
                  <div className={styles.userInitials}>
                    {initials}
                  </div>
                  <div className={styles.userDetails}>
                    <div className={styles.userName}>{displayName}</div>
                    {user?.email && <div className={styles.userEmail}>{user.email}</div>}
                  </div>
                </div>
              )}
              <div className={styles.footerActions}>
                {showViewToggle && (
                  <button type="button" onClick={() => navigate('/admin/dashboard')} className={styles.adminBackButton}>
                    ← Admin Dashboard
                  </button>
                )}
                <button type='button' onClick={logout}>
                  Log out
                </button>
              </div>
            </div>
          </aside>
      </>
    );
  };

  return (
    <div className={`${styles.app} ${theme === 'dark' ? styles.dark : styles.light} `}>
      <a href="#main-content" className="skip-link">Skip to content</a>  {/* SRS #8 / WCAG 2.4.1 */}
      {renderSidebar()}
      <main id="main-content" tabIndex={-1} className={`${styles.main} ${isDashboardView ? styles.mainDashboard : ''}`}>
        <NotificationDock />
        <Outlet context={{ selectedSessionId, setSelectedSessionId, refreshSessions }} />
      </main>
    </div>
  );
};
